/**
 * Shop multi-select + metric selector for the money-over-time chart (E2.1
 * spec 11.5): 0 or 1 shop selected keeps the existing 3-metric single-shop
 * view; 2+ shops switches MoneyChart to compare mode (one line per shop)
 * and reveals the metric select (Gross sales | Payout | Net profit,
 * default Gross sales). Apply-on-close (chart-filter follow-up): the shop
 * select keeps a LOCAL draft while its menu is open -- each checkbox tick
 * used to propagate to the parent immediately, firing one timeseries fetch
 * per tick -- and onShopIdsChange now fires ONCE, on menu close, and only
 * when the draft actually differs from the applied selection (set-equality,
 * so ticking and unticking the same shop inside one menu session is a
 * no-op).
 */
import { useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import { CHART_METRIC_OPTIONS, type ChartMetric, type ShopOption } from './chartHelpers.ts';

export interface ChartFiltersProps {
  shopOptions: readonly ShopOption[];
  selectedShopIds: readonly number[];
  onShopIdsChange: (ids: number[]) => void;
  metric: ChartMetric;
  onMetricChange: (metric: ChartMetric) => void;
  compareMode: boolean;
}

/** Order-insensitive equality for shop-id lists -- the "did the menu
 *  session actually change anything" gate, so tick+untick of the same shop
 *  never counts as a change (and never refetches). */
function sameIdSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((id) => set.has(id));
}

export function ChartFilters({
  shopOptions,
  selectedShopIds,
  onShopIdsChange,
  metric,
  onMetricChange,
  compareMode,
}: Readonly<ChartFiltersProps>): React.JSX.Element {
  // Apply-on-close draft (see file header). While the menu is open, ticks
  // only update this local draft; the parent hears about it once, on close.
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState<number[]>([...selectedShopIds]);
  if (!menuOpen && !sameIdSet(draft, selectedShopIds)) {
    // Parent changed the applied selection while the menu was closed --
    // adopt it. Safe to call during render (same pattern as DashboardPage's
    // currency sync): React de-dupes, and this only fires when the applied
    // set genuinely differs from the draft. SINGLE-WRITER ASSUMPTION: this
    // adoption only runs while the menu is CLOSED, so a parent that changed
    // selectedShopIds while the menu is OPEN would be clobbered by the
    // stale draft on close. Today our own onShopIdsChange is the sole
    // writer of that state, so the case is unreachable -- a future second
    // writer must close (or ignore) an open menu first.
    setDraft([...selectedShopIds]);
  }

  const value = draft.map(String);

  function handleShopsChange(e: SelectChangeEvent<string[]>): void {
    const raw = e.target.value;
    const selected = typeof raw === 'string' ? raw.split(',') : raw;
    setDraft(selected.filter((v) => v !== '').map(Number));
  }

  function handleClose(): void {
    setMenuOpen(false);
    if (!sameIdSet(draft, selectedShopIds)) onShopIdsChange([...draft]);
  }

  /** Owner ruling (round-3 review): the collapsed value must never grow the
   *  control - full names of 2+ shops made the select balloon and shove the
   *  layout around. 0 = All shops, 1 = that shop's label (ellipsized by the
   *  fixed width), 2+ = a count. The dropdown list still shows full labels. */
  function renderShopValue(selected: string[]): string {
    if (selected.length === 0) return 'All shops';
    if (selected.length === 1) {
      const one = shopOptions.find((s) => String(s.id) === selected[0]);
      return one ? one.label : '1 shop selected';
    }
    return `${selected.length} shops selected`;
  }

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Metric sits LEFT of the shop select and is ALWAYS rendered (merely
          disabled outside compare mode) so toggling compare mode never
          shifts the shop select's position - owner ruling, round-3 review. */}
      <FormControl size="small" sx={{ width: 160 }}>
        <InputLabel id="dashboard-chart-metric-label">Metric</InputLabel>
        <Select
          labelId="dashboard-chart-metric-label"
          label="Metric"
          value={metric}
          disabled={!compareMode}
          onChange={(e) => onMetricChange(e.target.value)}
          inputProps={{ 'aria-label': 'dashboard-chart-metric' }}
        >
          {CHART_METRIC_OPTIONS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: 250 }}>
        <InputLabel id="dashboard-shop-filter-label" shrink>
          Shop
        </InputLabel>
        <Select
          labelId="dashboard-shop-filter-label"
          label="Shop"
          notched
          multiple
          displayEmpty
          value={value}
          onChange={handleShopsChange}
          onOpen={() => setMenuOpen(true)}
          onClose={handleClose}
          renderValue={renderShopValue}
          sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
          inputProps={{ 'aria-label': 'dashboard-shop-filter' }}
        >
          {shopOptions.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              <Checkbox checked={draft.includes(s.id)} size="small" />
              <ListItemText primary={s.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default ChartFilters;
